var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BroadcastToPeerMessage, NetworkFacet, NetworkLocalOnly, PendingMessages, RemoveSubModelMessage, checkMatrix4Near } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { now } from "@messages/timing";
import { UpdateWorldTransformationMessage } from "@messages/scenegraph/ScenegraphMessages";
import { coreUserService } from "@modules/users";
import { busService } from "@modules/bus";
import { ModelEventHandler, ModelEventType, Service } from "@modules/module";
// TODO: This whole class is a hack!!!! Would be nice to solve this problem in a more streamlined way
class NetworkTransformService extends Service {
    constructor() {
        super(busService.connection("NetworkTransformService"));
        this.sendUpdateTime = 1000 / 25;
        this.lastSendUpdate = 0;
        this.currentTime = now();
        this.models = new Map();
        this.pendingBusMessages = new PendingMessages();
        /// Animation ///
        this.onUpdate = () => {
            this.interpolate();
        };
        this.onLateUpdate = () => {
            this.currentTime = now();
            if (this.currentTime - this.lastSendUpdate >= this.sendUpdateTime) {
                this.lastSendUpdate = this.currentTime;
            }
            this.flushMessages();
        };
        /// Message handlers ///
        this.handleNetworkFacet = (uid, facet) => {
            const model = this.getOrCreateModel(uid);
            const localOwner = coreUserService.getLocalUser().clientIdx;
            if (model.owner !== undefined && model.owner !== localOwner) {
                return;
            }
            if (model.owner === localOwner && facet.ownerId !== localOwner) {
                this.removePendingWorldTransformUpdates(uid);
            }
            model.owner = facet.ownerId;
            model.facet = facet;
        };
        this.handleRemoveNetworkFacet = (uid) => {
            this.removePendingWorldTransformUpdates(uid);
            const model = this.models.get(uid);
            if (model === undefined)
                return;
            model.facet = undefined;
        };
        this.handleRemoveSubModelMessage = (msg, meta) => {
            const model = this.models.get(msg.uid);
            if (model === undefined)
                return;
            if (model.createdTime > meta.timeStamp)
                return;
            this.models.delete(msg.uid);
        };
        this.handleNetworkLocalOnly = (msg) => {
            const model = this.getOrCreateModel(msg.uid);
            model.localOnly = true;
        };
        this.connection.subscribeByType(RemoveSubModelMessage, this.handleRemoveSubModelMessage);
        this.connection.subscribeByFacetType(NetworkFacet, this.handleNetworkFacet);
        this.connection.subscribeByRemoveFacetType(NetworkFacet, this.handleRemoveNetworkFacet);
        this.connection.subscribeByType(NetworkLocalOnly, this.handleNetworkLocalOnly);
        animationFrame$.add(this.onUpdate);
        animationFrame$.onLateUpdate$.add(this.onLateUpdate);
    }
    /// Manage network models ///
    getOrCreateModel(uid) {
        const model = this.models.get(uid);
        if (model !== undefined) {
            return model;
        }
        return this.createModel(uid);
    }
    createModel(uid, trans) {
        if (trans === undefined) {
            trans = new Matrix4();
        }
        const model = {
            createdTime: Date.now(),
            lastSendTime: 0,
            currentTransform: new Matrix4().copy(trans),
            previousTransform: new Matrix4().copy(trans),
            isDirty: true,
            isSync: true,
            localOnly: false
        };
        this.models.set(uid, model);
        return model;
    }
    interpolate() { }
    /// Messaging ///
    flushMessages() {
        if (this.pendingBusMessages.isEmpty())
            return;
        // ToDo send directly to peers
        this.connection.sendMultiple(this.pendingBusMessages.get());
        this.pendingBusMessages.clear();
    }
    removePendingWorldTransformUpdates(uid) {
        const remainingMessages = [];
        for (const message of this.pendingBusMessages.get()) {
            if (!BroadcastToPeerMessage.is(message)) {
                if (!UpdateWorldTransformationMessage.is(message) || message.uid !== uid) {
                    remainingMessages.push(message);
                }
                continue;
            }
            const peerMessages = message.msgs.filter(peerMessage => !UpdateWorldTransformationMessage.is(peerMessage) || peerMessage.uid !== uid);
            if (peerMessages.length > 0) {
                remainingMessages.push(new BroadcastToPeerMessage(peerMessages));
            }
        }
        this.pendingBusMessages.clear();
        this.pendingBusMessages.push(...remainingMessages);
    }
    handleModelTransformEvent(model, _localTransform) {
        const uid = model.getUID();
        const worldMatrix = model.getWorldTransform();
        const networkModel = this.models.get(uid);
        if (networkModel === undefined)
            return;
        const worldTransformChanged = !checkMatrix4Near(worldMatrix, networkModel.currentTransform, NetworkTransformService.WORLD_TRANSFORM_EPSILON);
        if (!worldTransformChanged)
            return;
        networkModel.previousTransform.copy(networkModel.currentTransform);
        networkModel.currentTransform.copy(worldMatrix);
        networkModel.lastSendTime = this.currentTime;
        networkModel.isDirty = false;
        if (networkModel.localOnly || networkModel.owner !== coreUserService.getLocalUser().clientIdx)
            return;
        // ToDo: For now we send a world transformation to the peers because the parent-child relationships are currently
        //  not being broad casted. It would probably be nicer if all relevant methods are being broadcasted to the peers.
        //  an option would be to specify which facets to broadcast and to broadcast everything is nothing was specified.
        //  then this service would check for each model event if this information should be broadcasted to the peers.
        this.pendingBusMessages.push(new BroadcastToPeerMessage([
            new UpdateWorldTransformationMessage(uid, worldMatrix.toArray())
        ]));
    }
    getUserType(owner) {
        return (owner === coreUserService.getLocalUser().clientIdx)
            ? "this user"
            : ((owner === undefined)
                ? "unknown user"
                : "remote user");
    }
}
NetworkTransformService.WORLD_TRANSFORM_EPSILON = 1e-5;
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], NetworkTransformService.prototype, "handleModelTransformEvent", null);
export const networkTransformService = new NetworkTransformService();
